import { ChangeEvent, useState } from 'react';
import { Crown, ShieldCheck, Send } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { StatusCodes } from 'http-status-codes';
import { getErrorMessage, getErrorStatus } from '@common/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '../ui/button';
import { OrganizationRoles, EPublishErrorMessage } from '@/enums';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '@/utils/stringHelpers';
import { useMutateRequestPublishProject } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import { useGetOrganizationMembers } from '@/reactQuery/get';
import { Member, OrgMembersMap } from '@/types';

interface RequestPublishingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function RequestPublishingModal({ isOpen, onClose }: RequestPublishingModalProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const {
    project_id: projectId,
    organization_id: organizationId,
    workspace_id: workspaceId,
  } = useParams();
  const { mutateAsync: mutateAsyncRequestPublishProject } = useMutateRequestPublishProject(
    organizationId,
    workspaceId,
  );
  const { lang } = useTranslation();

  const { data: { items: organizationMembers } = {} } = useGetOrganizationMembers(organizationId, {
    page: 1,
    limit: 250,
  });
  const filteredOwnerAndAdmins = organizationMembers?.filter(
    (item: Member) =>
      item?.email_verified &&
      item?.invite_accepted &&
      (item?.role === OrganizationRoles.OWNER || item?.role === OrganizationRoles.ADMIN),
  );

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (selectedOptions.includes(value)) {
      setSelectedOptions(selectedOptions.filter((option) => option !== value));
    } else {
      setSelectedOptions([...selectedOptions, value]);
    }
  };

  const requestPublishingProjectFunction = async (formData: {
    project_id: string | undefined;
    selected_users: { [key: string]: string[] };
  }) => {
    try {
      await mutateAsyncRequestPublishProject(formData);
      toast.success(lang.get('msg.organizationAdminsSuccessfullyInvited'));
      onClose();
    } catch (error) {
      // These axios instances reject with the unwrapped response, so read via
      // the getError* helpers (never error.response.X) — see getErrorDetails.ts.
      const status = getErrorStatus(error);
      const backendMessage = getErrorMessage(error);

      if (status === StatusCodes.TOO_MANY_REQUESTS) {
        toast.error(lang.get('msg.publishRequestRateLimit'));
      } else if (backendMessage === EPublishErrorMessage.PROJECT_NOT_READY) {
        // Backend returns 403 + this message (not the numeric 421 the old code
        // checked), so match on the message to surface the real reason.
        toast.error(lang.get('msg.someOfRequiredFieldsAreEmpty'));
      } else {
        toast.error(lang.get('errorPleaseTryAgain'));
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedOptions([]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="mb-1.5">
            {lang.get('msg.seekApprovalToPublishProject')}
          </DialogTitle>
          <DialogDescription>
            {lang.get('msg.requestPermissionFromTheOrganizations')} <b>{lang.get('msg.owner')}</b>{' '}
            {lang.get('msg.or')} <b>{lang.get('msg.admin')}</b>{' '}
            {lang.get('msg.toProceedWithPublishingTheProject')}.
          </DialogDescription>
        </DialogHeader>
        {/* content */}
        <div className="flex flex-col gap-2.5 mt-4 max-h-72 overflow-y-scroll p-1">
          {filteredOwnerAndAdmins?.map((item: OrgMembersMap) => (
            <label
              key={item._id}
              className={cn(
                'flex cursor-pointer items-center transition-all border-2 ease-in-out justify-between rounded-md ',
                selectedOptions.includes(item?.email) && 'border-primary/50 shadow',
              )}
            >
              <div className="flex items-center justify-between w-full h-full p-4">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src="" />
                    <AvatarFallback className="font-semibold border text-dark-text">
                      {getInitials(item?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col justify-center w-full">
                    <p className="flex items-center gap-1 text-base leading-5">
                      {item?.name}
                      {item?.role === OrganizationRoles.OWNER && (
                        <Crown size={16} className="text-yellow-500" />
                      )}
                      {item?.role === OrganizationRoles.ADMIN && (
                        <ShieldCheck size={15} className="text-gray-500" />
                      )}
                    </p>
                    <p className="text-sm">{item?.email}</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  value={item?.email}
                  checked={selectedOptions.includes(item?.email)}
                  onChange={handleOptionChange}
                  className="w-4 h-4"
                />
              </div>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end w-full mt-6">
          <DialogClose asChild>
            <Button
              variant="default"
              disabled={selectedOptions.length === 0}
              onClick={() =>
                requestPublishingProjectFunction({
                  project_id: projectId,
                  selected_users: {
                    emailsTo: selectedOptions,
                  },
                })
              }
              className="flex items-center gap-2"
            >
              <Send size={17} className="mt-px" /> {lang.get('msg.sendRequest')}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RequestPublishingModal;
